import React from 'react';
import { Phone, Scale, BookOpen, Truck, Users, AlertTriangle } from 'lucide-react';
export function GvwPage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-2607">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-2608">
        <div className="container mx-auto px-4" data-id="element-2609">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-2610">
            GVW Definitions
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-2611">
            Commercial Motor Vehicle Weight & Classification Standards
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-2612">
        <div className="max-w-4xl mx-auto" data-id="element-2613">
          {/* What Definitions Are Used */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2614">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2615">
              <BookOpen className="mr-3 text-[#B1050B]" size={28} data-id="element-2616" />
              What Definitions Are Used in This Part?
            </h2>

            <div className="space-y-6" data-id="element-2617">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-2618">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2619">
                  Commercial Motor Vehicle (CMV) Definition
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-2620">
                  A commercial motor vehicle is defined by any of the following
                  criteria:
                </p>

                <div className="space-y-4" data-id="element-2621">
                  <div className="bg-white rounded-lg p-4" data-id="element-2622">
                    <div className="flex items-start" data-id="element-2623">
                      <span className="bg-blue-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2624">
                        (1)
                      </span>
                      <div data-id="element-2625">
                        <p className="font-bold text-gray-900 mb-2" data-id="element-2626">
                          Weight Rating
                        </p>
                        <p className="text-gray-700" data-id="element-2627">
                          A gross vehicle weight (GVW), gross vehicle weight
                          rating (GVWR), gross combination weight (GCW), or
                          gross combination weight rating (GCWR) of{' '}
                          <strong data-id="element-2628">
                            4,537 kilograms (10,001 pounds) or more
                          </strong>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4" data-id="element-2629">
                    <div className="flex items-start" data-id="element-2630">
                      <span className="bg-green-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2631">
                        (2)
                      </span>
                      <div data-id="element-2632">
                        <p className="font-bold text-gray-900 mb-2" data-id="element-2633">
                          Passenger Capacity
                        </p>
                        <p className="text-gray-700" data-id="element-2634">
                          Regardless of weight, is designed or used to transport{' '}
                          <strong data-id="element-2635">
                            16 or more passengers, including driver
                          </strong>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4" data-id="element-2636">
                    <div className="flex items-start" data-id="element-2637">
                      <span className="bg-red-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2638">
                        (3)
                      </span>
                      <div data-id="element-2639">
                        <p className="font-bold text-gray-900 mb-2" data-id="element-2640">
                          Hazardous Materials
                        </p>
                        <p className="text-gray-700" data-id="element-2641">
                          Regardless of weight, is used in the transportation of
                          hazardous materials and is required to be placarded
                          pursuant to{' '}
                          <strong data-id="element-2642">49 CFR part 172, subpart F</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Weight Definitions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2643">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2644">
              <Scale className="mr-3 text-[#B1050B]" size={28} data-id="element-2645" />
              Weight Classification Definitions
            </h2>

            <div className="space-y-6" data-id="element-2646">
              <div className="bg-blue-50 rounded-lg p-6" data-id="element-2647">
                <h3 className="font-bold text-blue-900 mb-3 text-xl" data-id="element-2648">
                  GVW (Gross Vehicle Weight)
                </h3>
                <p className="text-gray-700" data-id="element-2649">
                  The actual weight of a vehicle and its load
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-2650">
                <h3 className="font-bold text-green-900 mb-3 text-xl" data-id="element-2651">
                  GVWR (Gross Vehicle Weight Rating)
                </h3>
                <p className="text-gray-700" data-id="element-2652">
                  The maximum weight a vehicle is designed to carry, as
                  specified by the manufacturer
                </p>
              </div>

              <div className="bg-yellow-50 rounded-lg p-6" data-id="element-2653">
                <h3 className="font-bold text-yellow-900 mb-3 text-xl" data-id="element-2654">
                  GCW (Gross Combination Weight)
                </h3>
                <p className="text-gray-700" data-id="element-2655">
                  The actual total weight of a combination of vehicles (e.g.,
                  truck and trailer)
                </p>
              </div>

              <div className="bg-purple-50 rounded-lg p-6" data-id="element-2656">
                <h3 className="font-bold text-purple-900 mb-3 text-xl" data-id="element-2657">
                  GCWR (Gross Combination Weight Rating)
                </h3>
                <p className="text-gray-700" data-id="element-2658">
                  The maximum weight a combination of vehicles is designed to
                  carry, as specified by the manufacturer
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 mt-6" data-id="element-2659">
                <p className="font-bold text-gray-900 mb-2" data-id="element-2660">
                  Critical Threshold:
                </p>
                <p className="text-gray-700" data-id="element-2661">
                  The key weight threshold for CMV classification is{' '}
                  <strong data-id="element-2662">10,001 pounds (4,537 kg) or more</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Key Program Definitions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2663">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2664">
              Key Program Definitions
            </h2>

            <div className="space-y-6" data-id="element-2665">
              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2666">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2667">
                  Commercial Vehicle Safety Plan (CVSP)
                </h3>
                <p className="text-gray-700" data-id="element-2668">
                  The document outlining the State's CMV safety objectives,
                  strategies, activities and performance measures.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2669">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2670">
                  Compatible or Compatibility
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-2671">
                  State laws and regulations applicable to interstate commerce
                  and to intrastate movement of hazardous materials are
                  identical to the FMCSRs and the HMRs or have the same effect
                  as the FMCSRs.
                </p>
                <p className="text-gray-700" data-id="element-2672">
                  State laws applicable to intrastate commerce are either
                  identical to, or have the same effect as, the FMCSRs or fall
                  within the established limited variances under §350.341.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2673">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2674">
                  High Priority Activity Funds
                </h3>
                <p className="text-gray-700" data-id="element-2675">
                  Funds provided to States, local governments, and other persons
                  carrying out activities and projects that directly support the
                  MCSAP, are national in scope in that the successful activity
                  or project could potentially be applied in other States on a
                  national scale, and improve CMV safety and compliance with CMV
                  safety regulations. Up to <strong data-id="element-2676">5 percent</strong> of total
                  MCSAP funds are available for these activities.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2677">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2678">
                  Incentive Funds
                </h3>
                <p className="text-gray-700" data-id="element-2679">
                  Funds awarded to States achieving reductions in CMV involved
                  fatal accidents, CMV fatal accident rate, or meeting specified
                  CMV safety program performance criteria.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2680">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2681">
                  Operating Authority
                </h3>
                <p className="text-gray-700" data-id="element-2682">
                  The registration required by 49 U.S.C. 13902, 49 CFR part 365,
                  49 CFR part 368, and 49 CFR 392.9a.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2683">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2684">Large Truck</h3>
                <p className="text-gray-700" data-id="element-2685">
                  A truck over{' '}
                  <strong data-id="element-2686">10,000 pounds gross vehicle weight rating</strong>{' '}
                  including single unit trucks and truck tractors (FARS
                  definition).
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2687">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2688">Motor Carrier</h3>
                <p className="text-gray-700" data-id="element-2689">
                  A for-hire motor carrier or private motor carrier. The term
                  includes a motor carrier's agents, officers, or
                  representatives responsible for hiring, supervising, training,
                  assigning, or dispatching a driver or concerned with the
                  installation, inspection, and maintenance of motor vehicle
                  equipment or accessories or both.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2690">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2691">
                  North American Standard Inspection
                </h3>
                <p className="text-gray-700" data-id="element-2692">
                  The methodology used by State CMV safety inspectors to conduct
                  safety inspections of CMVs. This consists of various levels of
                  inspection of the vehicle or driver or both. The inspection
                  criteria are developed by the FMCSA in conjunction with the
                  Commercial Vehicle Safety Alliance (CVSA), an association of
                  States, Canadian Provinces, and Mexico whose members agree to
                  adopt these standards for inspecting CMVs in their
                  jurisdiction.
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-600 mt-6 italic" data-id="element-2693">
              [65 FR 15102, Mar. 21, 2000; 71 FR 50866, Aug. 28, 2006]
            </p>
          </div>

          {/* Quick Reference */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-2694">
            <h2 className="text-3xl font-bold mb-6" data-id="element-2695">
              Quick Reference: CMV Classification
            </h2>

            <div className="space-y-4" data-id="element-2696">
              <div className="bg-white/10 rounded-lg p-6" data-id="element-2697">
                <div className="flex items-start" data-id="element-2698">
                  <Scale className="mr-3 flex-shrink-0 mt-1" size={24} data-id="element-2699" />
                  <div data-id="element-2700">
                    <p className="font-bold mb-2" data-id="element-2701">Weight Threshold</p>
                    <p className="text-gray-200" data-id="element-2702">
                      10,001+ pounds (4,537+ kg) GVW/GVWR/GCW/GCWR
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-2703">
                <div className="flex items-start" data-id="element-2704">
                  <Users className="mr-3 flex-shrink-0 mt-1" size={24} data-id="element-2705" />
                  <div data-id="element-2706">
                    <p className="font-bold mb-2" data-id="element-2707">Passenger Capacity</p>
                    <p className="text-gray-200" data-id="element-2708">
                      16+ passengers (including driver), regardless of weight
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-2709">
                <div className="flex items-start" data-id="element-2710">
                  <AlertTriangle className="mr-3 flex-shrink-0 mt-1" size={24} data-id="element-2711" />
                  <div data-id="element-2712">
                    <p className="font-bold mb-2" data-id="element-2713">Hazardous Materials</p>
                    <p className="text-gray-200" data-id="element-2714">
                      Placarded hazmat (49 CFR 172, subpart F), regardless of
                      weight
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-2715">
                <div className="flex items-start" data-id="element-2716">
                  <Truck className="mr-3 flex-shrink-0 mt-1" size={24} data-id="element-2717" />
                  <div data-id="element-2718">
                    <p className="font-bold mb-2" data-id="element-2719">Large Truck Definition</p>
                    <p className="text-gray-200" data-id="element-2720">
                      Over 10,000 lbs GVWR (single units and truck tractors)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Takeaways */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-2721">
            <h2 className="text-3xl font-bold mb-6" data-id="element-2722">Key Takeaways</h2>

            <div className="space-y-3" data-id="element-2723">
              <div className="flex items-start" data-id="element-2724">
                <span className="mr-3 flex-shrink-0 mt-1" data-id="element-2725">✓</span>
                <span data-id="element-2726">
                  CMV defined by weight (10,001+ lbs), passenger capacity (16+),
                  or placarded hazmat
                </span>
              </div>
              <div className="flex items-start" data-id="element-2727">
                <span className="mr-3 flex-shrink-0 mt-1" data-id="element-2728">✓</span>
                <span data-id="element-2729">
                  GVW = actual weight; GVWR = manufacturer's maximum rating
                </span>
              </div>
              <div className="flex items-start" data-id="element-2730">
                <span className="mr-3 flex-shrink-0 mt-1" data-id="element-2731">✓</span>
                <span data-id="element-2732">
                  GCW = actual combination weight; GCWR = manufacturer's
                  combination rating
                </span>
              </div>
              <div className="flex items-start" data-id="element-2733">
                <span className="mr-3 flex-shrink-0 mt-1" data-id="element-2734">✓</span>
                <span data-id="element-2735">
                  Large truck = over 10,000 lbs GVWR (FARS definition)
                </span>
              </div>
              <div className="flex items-start" data-id="element-2736">
                <span className="mr-3 flex-shrink-0 mt-1" data-id="element-2737">✓</span>
                <span data-id="element-2738">
                  North American Standard Inspection developed by FMCSA and CVSA
                </span>
              </div>
              <div className="flex items-start" data-id="element-2739">
                <span className="mr-3 flex-shrink-0 mt-1" data-id="element-2740">✓</span>
                <span data-id="element-2741">State laws must be compatible with FMCSRs and HMRs</span>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-2742">
            <h2 className="text-3xl font-bold mb-4" data-id="element-2743">
              Need Help Understanding GVW Requirements?
            </h2>
            <p className="text-xl mb-6" data-id="element-2744">
              Expert guidance on weight classifications and CMV compliance
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-2745">
              <Phone className="mr-3" size={24} data-id="element-2746" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-2747">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}